import { AppNS } from 'app';
import { apiUpdateUserTerms } from 'app/api_v2/user';
import { isLoggedInAsAdmin } from 'app/helpers/isLoggedInAsAdmin';
import { isBackoffice, isGeneral } from 'app/helpers/userRoles';
import { useAppDispatch, useAppSelector } from 'app/store';
import { updateUserData } from 'app/store/auth';
import { RootState } from 'app/store/rootReducer';
import ButtonLoader from 'app/ui_v2/components/button-loader/ButtonLoader';
import ErrorsList from 'app/ui_v2/components/errors-list/ErrorsList';
import { useEffect, useState } from 'react';

import { Box, Button, Card, CardContent, Checkbox, FormControlLabel, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';

const isShowOverlay = (user: AppNS.User): boolean => {
  if (!user) {
    return false;
  }

  // To Show T&C Overlay:

  // user cannot be admin
  if (isBackoffice(user) || isGeneral(user)) {
    return false;
  }

  // user cannot be logged in as
  if (isLoggedInAsAdmin()) {
    return false;
  }

  // user can't have already accepted terms
  if (user.termsAcceptedAt) {
    return false;
  }

  return true;
};

export default function TermsOverlay() {
  const { user } = useAppSelector((state: RootState) => state.auth);
  const dispatch = useAppDispatch();

  const onSubmit = () => {
    mutate();
  };

  const { isPending, isError, error, mutate } = useMutation({
    mutationFn: () => apiUpdateUserTerms(),
    onSuccess: (response: string) => {
      // update auth store
      dispatch(
        updateUserData({
          termsAcceptedAt: response,
        }),
      );

      setOpen(false);
    },
  });

  const [open, setOpen] = useState(isShowOverlay(user));
  const [checked, isChecked] = useState(false);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    isChecked(event.target.checked);
  };

  useEffect(() => {
    setOpen(isShowOverlay(user));
  }, [user]);

  if (!open) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        background: 'rgba(33, 58, 109, 0.75)',
        zIndex: 9000,
      }}
      display={open ? 'flex' : 'none'}
      justifyContent="center"
      alignItems="center"
    >
      <Box maxWidth={700}>
        <Card>
          <CardContent>
            <Box textAlign="center">
              <Typography variant="h2">Updated terms and conditions</Typography>
            </Box>
            <Box fontSize="2.4rem" textAlign="center">
              <p>
                We have updated our{' '}
                <a href="/legal" rel="noopener noreferrer" target="_blank">
                  Terms and Conditions
                </a>
                .
                <br />
                To continue accessing and using Tertius, please review and accept the updated terms.
              </p>
            </Box>
            <Box mb={2} textAlign="center">
              <FormControlLabel
                control={<Checkbox checked={checked} onChange={handleChange} name="tc" />}
                label={<Box fontSize="1.6rem">I have read and accept the updated Terms and Conditions.</Box>}
              />
            </Box>
            <Box width="100%" display="flex" justifyContent="center">
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  onSubmit();
                }}
                disableElevation
                disabled={!checked}
              >
                <ButtonLoader isActive={isPending} />
                Continue
              </Button>
            </Box>
            {isError && <ErrorsList title="Update Terms and Conditions Error" errors={error as string[]} />}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}
